import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, describeApiError } from "@/lib/api";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { ConfidenceBadge } from "@/components/ConfidenceBadge";
import { CircleNotch, ArrowsLeftRight, Repeat, Warning, Sparkle } from "@phosphor-icons/react";

const money = (n) => `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString("en-US", sameYear ? { month: "short", day: "numeric" } : { month: "short", day: "numeric", year: "numeric" });
};

const FLAG_META = {
  transfer: { label: "Transfer", bg: "#E8F0FB", color: "#2B5EA7", icon: ArrowsLeftRight },
  subscription: { label: "Subscription", bg: "#F0EBFA", color: "#6B4BA3", icon: Repeat },
  unusual_charge: { label: "Unusual Charge", bg: "#FFF4D9", color: "#8A6A3B", icon: Warning },
};

const ACTIONS = [
  { key: "confirm", label: "Confirm as categorized" },
  { key: "edit_category", label: "Edit category" },
  { key: "mark_as_transfer", label: "Mark as transfer" },
  { key: "dismiss", label: "Dismiss flag" },
];

function summaryChip(session) {
  const c = session.counts_by_type || {};
  const parts = [];
  if (c.transfer) parts.push(`${c.transfer} transfer${c.transfer !== 1 ? "s" : ""}`);
  if (c.subscription) parts.push(`${c.subscription} subscription${c.subscription !== 1 ? "s" : ""}`);
  if (c.unusual_charge) parts.push(`${c.unusual_charge} unusual charge${c.unusual_charge !== 1 ? "s" : ""}`);
  return `Rowan flagged ${session.flagged_count} transaction${session.flagged_count !== 1 ? "s" : ""}${parts.length ? ` — ${parts.join(", ")}` : ""}`;
}

function FlaggedCard({ record, catOptions, busy, onAction }) {
  const meta = FLAG_META[record.flag_type] || FLAG_META.unusual_charge;
  const Icon = meta.icon;
  const [pickerOpen, setPickerOpen] = useState(false);
  const action = record.user_action || "none";
  const isIncome = record.txn_type === "income";
  const selectedStyle = (key) => {
    if (action !== key) return { borderColor: "var(--bk-border)", color: "var(--bk-text)", background: "#fff" };
    if (key === "confirm") return { borderColor: "var(--bk-accent)", background: "var(--bk-accent)", color: "#fff" };
    if (key === "mark_as_transfer") return { borderColor: "#2B5EA7", background: "#2B5EA7", color: "#fff" };
    if (key === "dismiss") return { borderColor: "#6B7280", background: "#6B7280", color: "#fff" };
    return { borderColor: "var(--bk-accent)", color: "var(--bk-accent)", background: "#fff" };
  };

  return (
    <div className="rounded-[20px] border bg-white p-4" style={{ borderColor: "var(--bk-border)" }} data-testid={`review-card-${record.transaction_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-display text-lg font-bold" style={{ color: isIncome ? "var(--bk-success)" : "var(--bk-text)" }} data-testid={`review-amount-${record.transaction_id}`}>
            {isIncome ? "+" : ""}{money(record.amount)}
          </p>
          <p className="text-sm font-medium mt-0.5" style={{ color: "var(--bk-text)" }} data-testid={`review-payee-${record.transaction_id}`}>{record.description}</p>
          <p className="text-xs mt-0.5" style={{ color: "var(--bk-text-2)" }} data-testid={`review-date-${record.transaction_id}`}>{fmtDate(record.date)} · {record.account_name}</p>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold" style={{ background: meta.bg, color: meta.color }} data-testid={`review-flag-badge-${record.transaction_id}`}>
            <Icon size={12} weight="bold" /> {meta.label}
          </span>
          <ConfidenceBadge
            confidence={record.confidence}
            reason={record.flag_reason}
            category={record.resolved_category || record.suggested_category}
            originalCategory={record.suggested_category}
            categoryOptions={catOptions}
            onSelectCategory={(c) => onAction("edit_category", c)}
            testId={`review-confidence-${record.transaction_id}`}
          />
        </div>
      </div>

      <ReasonLine reason={record.flag_reason} testId={`review-reason-${record.transaction_id}`} />

      <p className="text-xs mt-2" style={{ color: "var(--bk-text-2)" }}>
        Suggested category: <span className="font-semibold" style={{ color: "var(--bk-text)" }}>{record.suggested_category || "—"}</span>
        {action === "edit_category" && record.resolved_category && record.resolved_category !== record.suggested_category && (
          <span className="ml-1 font-semibold" style={{ color: "var(--bk-accent)" }} data-testid={`review-edited-category-${record.transaction_id}`}>→ {record.resolved_category}</span>
        )}
      </p>

      <div className="flex flex-wrap gap-1.5 mt-3">
        {ACTIONS.map((a) => (
          <button
            key={a.key}
            type="button"
            disabled={busy}
            onClick={() => {
              if (a.key === "edit_category") { setPickerOpen((v) => !v); return; }
              setPickerOpen(false);
              onAction(a.key, null);
            }}
            data-testid={`review-action-${a.key}-${record.transaction_id}`}
            className="px-2.5 py-1.5 rounded-full text-[11px] font-semibold border transition-colors disabled:opacity-60"
            style={selectedStyle(a.key)}
          >
            {a.key === "edit_category" && action === "edit_category" && record.resolved_category ? `Category: ${record.resolved_category}` : a.label}
          </button>
        ))}
      </div>

      {pickerOpen && (
        <select
          autoFocus
          defaultValue={record.resolved_category || record.suggested_category || ""}
          onChange={(e) => { onAction("edit_category", e.target.value); setPickerOpen(false); }}
          data-testid={`review-category-picker-${record.transaction_id}`}
          className="mt-2 w-full rounded-md border px-2.5 py-2 text-sm bg-white outline-none"
          style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}
        >
          <option value="" disabled>Pick a category…</option>
          {catOptions.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      )}

      {action === "none" && (
        <p className="text-[11px] mt-2.5 rounded-md px-2.5 py-1.5" style={{ background: "var(--bk-page)", color: "var(--bk-text-2)" }} data-testid={`review-unreviewed-notice-${record.transaction_id}`}>
          No action taken — will import as-is.
        </p>
      )}
    </div>
  );
}

function ReasonLine({ reason, testId }) {
  const [expanded, setExpanded] = useState(false);
  const text = (reason || "").trim() || "Rowan flagged this transaction for review.";
  const truncated = text.length > 80 && !expanded;
  return (
    <p className="text-sm mt-2 flex items-start gap-1.5" style={{ color: "var(--bk-text-2)" }} data-testid={testId}>
      <Sparkle size={14} weight="fill" color="var(--bk-gold)" className="shrink-0 mt-0.5" />
      <span>
        {truncated ? `${text.slice(0, 80)}…` : text}
        {truncated && (
          <button type="button" onClick={() => setExpanded(true)} className="ml-1 text-xs font-semibold hover:underline" style={{ color: "var(--bk-accent)" }}>more</button>
        )}
      </span>
    </p>
  );
}

export function PlaidImportReview({ open, session, businessId, onClose, onSessionUpdate, onFinalized }) {
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 640);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  const [cats, setCats] = useState({ income: [], expense: [] });
  const [busyTxn, setBusyTxn] = useState("");
  const [finalizing, setFinalizing] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get(`/businesses/${businessId}/categories`).then((r) => setCats(r.data)).catch(() => {});
  }, [open, businessId]);

  const flagged = session?.flagged || [];
  const reviewedCount = useMemo(() => flagged.filter((r) => (r.user_action || "none") !== "none").length, [flagged]);

  const applyAction = async (record, userAction, resolvedCategory) => {
    setBusyTxn(record.transaction_id);
    try {
      const { data } = await api.post(`/businesses/${businessId}/plaid/review-session/${session.session_id}/action`, {
        transaction_id: record.transaction_id,
        user_action: userAction,
        resolved_category: resolvedCategory || null,
      });
      onSessionUpdate(data.session);
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setBusyTxn("");
    }
  };

  const finalize = async () => {
    setFinalizing(true);
    try {
      const { data } = await api.post(`/businesses/${businessId}/plaid/review-session/${session.session_id}/finalize`);
      toast.success(data?.rowan_message || "Import complete.");
      onFinalized();
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setFinalizing(false);
    }
  };

  if (!session) return null;

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent
        side={isMobile ? "bottom" : "right"}
        className={`w-full p-0 flex flex-col gap-0 ${isMobile ? "h-[100dvh]" : "sm:max-w-[40vw] sm:min-w-[480px]"}`}
        style={{ background: "var(--bk-page)" }}
        data-testid="import-review-drawer"
      >
        <div className="px-5 pt-5 pb-4 border-b bg-white" style={{ borderColor: "var(--bk-border)" }}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }} data-testid="review-drawer-title">Review Import</h2>
              <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }} data-testid="review-drawer-subtitle">
                Rowan found {session.flagged_count} transaction{session.flagged_count !== 1 ? "s" : ""} to check before we write them to your books.
              </p>
            </div>
            <button type="button" onClick={onClose} data-testid="review-later-link" className="text-sm font-semibold hover:underline shrink-0 mr-6" style={{ color: "var(--bk-accent)" }}>
              Review Later
            </button>
          </div>
          <div className="mt-3 inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold" style={{ background: "var(--bk-rowan-bg)", color: "#8A5A13" }} data-testid="review-summary-chip">
            {summaryChip(session)}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3" data-testid="review-flagged-list">
          {flagged.map((record) => (
            <FlaggedCard
              key={record.transaction_id}
              record={record}
              catOptions={record.txn_type === "income" ? (cats.income || []) : (cats.expense || [])}
              busy={busyTxn === record.transaction_id}
              onAction={(action, cat) => applyAction(record, action, cat)}
            />
          ))}
        </div>

        <div className="px-5 py-4 border-t bg-white space-y-2" style={{ borderColor: "var(--bk-border)" }}>
          <div className="flex items-center justify-between text-xs" style={{ color: "var(--bk-text-2)" }}>
            <span data-testid="review-progress">{reviewedCount} of {flagged.length} reviewed</span>
            <span data-testid="review-unflagged-note">Unflagged transactions ({session.unflagged_count}) will import automatically.</span>
          </div>
          <button
            type="button"
            onClick={finalize}
            disabled={finalizing}
            data-testid="finalize-import-button"
            className="bk-btn-primary w-full sm:w-auto sm:ml-auto sm:flex px-5 py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-70"
          >
            {finalizing ? <><CircleNotch size={16} className="animate-spin" /> Finalizing…</> : "Finalize Import"}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
